import { EventModel } from "@nova/db-table-live-video"
import { LiveVideoFlight } from "@nova/protobuf"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { PluginSpec, staticIcon } from "plugins/plugin-registry"
import { selectOpenVideoFlights } from "plugins/Video/state/selectors"
import { openVideoFlight } from "plugins/Video/state/thunks"
import { useEffect, useMemo, useState } from "react"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { fetchTelemetryEvents } from "tools/telemetry/fetchTelemetryEvents"

export const getUseLiveVideoMapNodes = (spec: PluginSpec<LiveVideoFlight>) => {
    const useLiveVideoMapNodes = () => {
        const dispatch = useAppDispatch()
        const [startingPositions, setStartingPositions] = useState<
            Record<string, { lat: number; lng: number }>
        >({})
        const entities = useAppSelector(spec.selectAllEntities)
        const recordedLiveStreamIdsKey = useMemo(
            () =>
                entities
                    .filter((entity) => !entity.live && !!entity.recordingPrefix)
                    .map((entity) => entity.id)
                    .toSorted((a, b) => a.localeCompare(b))
                    .join(","),
            [entities]
        )
        const mobileLiveStreamsWithInitialCoordinateIdsKey = useMemo(
            () =>
                entities
                    .filter((entity) => !!entity.pilotInitialCoordinate)
                    .map((entity) => entity.id)
                    .toSorted((a, b) => a.localeCompare(b))
                    .join(","),
            [entities]
        )

        const entityById = useMemo(
            () =>
                entities.reduce<Record<string, LiveVideoFlight>>((acc, cur) => {
                    acc[cur.id] = cur
                    return acc
                }, {}),
            [entities]
        )

        const openVideoFlightIds = useAppSelector(selectOpenVideoFlights)
        const orgId = useAppSelector(selectActiveWorkspaceId())
        const projectId = useAppSelector(selectActivatedFireId)

        useEffect(() => {
            if (!orgId || !projectId) return
            const get = async () => {
                const newMap: typeof startingPositions = {}
                for (const id of recordedLiveStreamIdsKey.split(",")) {
                    if (!id) continue
                    const { events } = await fetchTelemetryEvents({ orgId, projectId, id })
                    const firstEvent = (events as EventModel[]).reduce(
                        (acc, cur) => {
                            if (!cur.latitude || !cur.longitude) return acc
                            if (!acc || cur.timestamp < acc.timestamp) {
                                return cur
                            }
                            return acc
                        },
                        undefined as EventModel | undefined
                    )
                    if (firstEvent) {
                        newMap[id] = { lat: firstEvent.latitude, lng: firstEvent.longitude }
                    }
                }
                setStartingPositions((prev) => ({ ...prev, ...newMap }))
            }
            get()
        }, [recordedLiveStreamIdsKey, orgId, projectId])

        useEffect(() => {
            const newMap: typeof startingPositions = {}
            for (const id of mobileLiveStreamsWithInitialCoordinateIdsKey.split(",")) {
                if (!id) continue
                const flight = entityById[id]
                if (!flight.pilotInitialCoordinate) continue
                const { longitude, latitude } = flight.pilotInitialCoordinate
                newMap[id] = { lat: latitude, lng: longitude }
            }
            setStartingPositions((prev) => ({ ...prev, ...newMap }))
        }, [mobileLiveStreamsWithInitialCoordinateIdsKey, entityById])

        const entityData = useMemo(() => {
            return entities
                .filter((entity) => {
                    return (
                        startingPositions[entity.id] &&
                        !entity.archivedTimestamp &&
                        !entity.live &&
                        !openVideoFlightIds.includes(entity.id)
                    )
                })
                .map((entity) => {
                    return {
                        id: entity.id,
                        position: startingPositions[entity.id],
                        visible: entity.visible,
                        locked: !!entity.locked,
                        active: openVideoFlightIds.includes(entity.id),
                        onClick: () => {
                            dispatch(openVideoFlight({ flightId: entity.id }))
                        },
                        iconSrc: staticIcon({
                            default: "/icons/liveVideo/default.svg",
                            hover: "/icons/liveVideo/hover.svg",
                        }),
                    }
                })
        }, [dispatch, entities, openVideoFlightIds, startingPositions])
        return entityData
    }
    return useLiveVideoMapNodes
}
